import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Target,
  Landmark,
  FileText,
  PenTool,
  GraduationCap,
  Banknote,
  Plane,
  Home,
  CheckCircle2,
  BookOpen,
  Users,
  Award,
  Globe,
  ArrowRight,
  Sparkles
} from 'lucide-react';
const Services = () => {
  // Reserved for future use:
  // const fadeInUp = {
  //   hidden: { opacity: 0, y: 30 },
  //   visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  // };

  const studyAbroadServices = [
    {
      step: "01",
      title: 'Career Guidance & Course Selection',
      description: 'Choosing the right career path is the foundation of a successful future. At 6th Sense Academy, our certified counselors provide personalized career counseling sessions to help students identify their strengths, interests and academic goals. We use psychometric analysis, one-on-one discussions, and global education insights to guide students toward the most suitable course and country.',
      icon: Target,
      link: '/admissions'
    },
    {
      step: "02",
      title: 'Shortlisting Universities',
      description: 'Selecting the right university can be overwhelming, given the vast number of options worldwide. Our experts carefully shortlist universities based on academic background, course specialization, rankings, budget, and career prospects. We help students choose universities where they have maximum chances of admission and scholarships.',
      icon: Landmark,
      link: '/partnered-universities'
    },
    {
      step: "03",
      title: 'Application & Admission Process',
      description: 'Our team simplifies the complex admission process by offering step-by-step assistance from start to finish. We help students with creating application portals, document preparation, meeting deadlines, and tracking application status with universities.',
      icon: FileText,
      link: '/admissions'
    },
    {
      step: "04",
      title: 'LOR & SOP Preparation',
      description: 'A well-written Letter of Recommendation and Statement of Purpose can strongly influence the admission decision. Our counselors provide guidance on choosing the right recommenders and crafting personalized, impactful documents that meet the standards of top global universities.',
      icon: PenTool,
      link: '/admissions'
    },
    {
      step: "05",
      title: 'Scholarship Guidance',
      description: 'We believe finances should never stop a dream. Our scholarship experts guide students to explore and apply for various university-based, government, and external scholarships. We help every student maximize financial aid opportunities and minimize the cost of studying abroad.',
      icon: GraduationCap,
      link: '/admissions'
    },
    {
      step: "06",
      title: 'Loan Assistance',
      description: 'Financing higher education abroad can be challenging, but we make it simple. 6th Sense Academy has official tie-ups with leading education loan providers. Our finance team assists in comparing loan options, preparing documentation, and ensuring timely loan approval and disbursement.',
      icon: Banknote,
      link: '/admissions'
    },
    {
      step: "07",
      title: 'Visa Guidance',
      description: 'Getting a visa approved is a crucial step in the process, and our visa experts have an excellent track record of success. We provide step-by-step guidance on visa application procedures, documentation assistance, personalized mock interviews, and updated country-specific visa regulations.',
      icon: Plane,
      link: '/visa-success-stories'
    },
    {
      step: "08",
      title: 'Pre-Departure & Post-Arrival Support',
      description: 'Our support doesn\'t end with visa approval. We provide comprehensive pre-departure briefings covering accommodation, travel arrangements, and cultural orientation. We also offer post-arrival support to help students settle in their new country smoothly.',
      icon: Home,
      link: '/admissions'
    },
  ];

  const trainingServices = [
    {
      title: 'IELTS Training',
      description: 'Comprehensive IELTS preparation for Academic, General, and UKVI tests. Expert trainers, personalized coaching, and proven strategies to achieve your target band score.',
      icon: BookOpen,
      link: '/courses',
      features: ['Academic & General Training', 'UKVI Preparation', 'Mock Tests', 'Score Guarantee']
    },
    {
      title: 'OET Training',
      description: 'Specialized OET preparation for healthcare professionals. Tailored training for doctors, nurses, and other medical professionals seeking to work in English-speaking countries.',
      icon: Users,
      link: '/courses',
      features: ['Healthcare Specific', 'All Modules Covered', 'Practice Tests', 'Expert Guidance']
    },
    {
      title: 'PTE Training',
      description: 'Complete PTE Academic preparation with computer-based test strategies. Master all sections with our comprehensive training program designed for quick results.',
      icon: Award,
      link: '/courses',
      features: ['Academic & UKVI', 'Computer-Based Prep', 'Real Exam Practice', 'Fast Results']
    },
    {
      title: 'DET Training',
      description: 'Duolingo English Test preparation for modern, convenient English proficiency testing. Quick and effective training to achieve your target score.',
      icon: Globe,
      link: '/courses',
      features: ['Online Test Prep', 'Adaptive Learning', 'Quick Results', 'Affordable']
    },
  ];

  return (
    <div className="bg-slate-50 min-h-screen font-sans">
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-16 bg-slate-900 text-white overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden opacity-20">
          <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-red-600/30 rounded-full blur-[120px] -translate-y-1/2 translate-x-1/2"></div>
          <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-blue-600/20 rounded-full blur-[100px] translate-y-1/2 -translate-x-1/2"></div>
        </div>

        <div className="max-w-7xl mx-auto px-6 relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-600/20 mb-6">
              <Sparkles size={32} className="text-red-500" />
            </div>
            <h1 className="text-4xl md:text-5xl font-serif font-bold mb-4 text-gray-100">
              Our Services
            </h1>
            <p className="text-xl text-slate-300 max-w-2xl mx-auto">
              Comprehensive solutions for your study abroad journey and language training needs
            </p>
          </motion.div>
        </div>
      </section>

      {/* Study Abroad Services */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-red-600 font-bold tracking-widest uppercase text-xs mb-3 block">Study Abroad</span>
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-slate-900 mb-4">
              Complete Study Abroad Solutions
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              From career guidance to visa approval, we provide end-to-end support for your international education journey
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {studyAbroadServices.map((service, index) => {
              const Icon = service.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="group bg-slate-50 rounded-2xl p-8 border border-slate-200 hover:bg-white hover:border-red-200 hover:shadow-xl transition-all duration-300"
                >
                  <div className="flex items-start gap-4 mb-4">
                    <div className="w-14 h-14 rounded-xl bg-red-50 flex items-center justify-center text-red-600 group-hover:scale-110 transition-transform flex-shrink-0">
                      <Icon size={24} />
                    </div>
                    <div className="flex-1">
                      <div className="text-xs font-bold text-red-600 mb-2">{service.step}</div>
                      <h3 className="text-xl font-serif font-bold text-slate-900 mb-2">
                        {service.title}
                      </h3>
                    </div>
                  </div>
                  <p className="text-slate-600 leading-relaxed mb-4">
                    {service.description}
                  </p>
                  <Link
                    to={service.link}
                    className="inline-flex items-center gap-2 text-red-600 font-semibold text-sm hover:gap-3 transition-all"
                  >
                    Learn More <ArrowRight size={16} />
                  </Link>
                </motion.div>
              );
            })}
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="mt-12 text-center"
          >
            <Link
              to="/book-free-counselling"
              className="inline-flex items-center gap-2 bg-red-600 text-white px-8 py-4 rounded-lg font-bold hover:bg-red-700 transition-colors shadow-lg shadow-red-600/20"
            >
              Book Free Counselling <ArrowRight size={20} />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Training Services */}
      <section className="py-20 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-red-500 font-bold tracking-widest uppercase text-xs mb-3 block">Language Training</span>
            <h2 className="text-3xl md:text-4xl font-serif font-bold mb-4">
              Expert Language Training
            </h2>
            <p className="text-slate-300 max-w-2xl mx-auto">
              Comprehensive test preparation for IELTS, OET, PTE, and DET with proven strategies and expert guidance
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {trainingServices.map((service, index) => {
              const Icon = service.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="group bg-slate-800/50 rounded-2xl p-6 border border-slate-700 hover:border-red-500/50 hover:bg-slate-800 transition-all duration-300"
                >
                  <div className="w-12 h-12 rounded-lg bg-red-600/20 flex items-center justify-center text-red-500 mb-4 group-hover:scale-110 transition-transform">
                    <Icon size={24} />
                  </div>
                  <h3 className="text-xl font-serif font-bold mb-3">
                    {service.title}
                  </h3>
                  <p className="text-slate-300 text-sm leading-relaxed mb-4">
                    {service.description}
                  </p>
                  <ul className="space-y-2 mb-4">
                    {service.features.map((feature, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm text-slate-400">
                        <CheckCircle2 size={14} className="text-red-500 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <Link
                    to={service.link}
                    className="inline-flex items-center gap-2 text-red-400 font-semibold text-sm hover:text-red-300 hover:gap-3 transition-all"
                  >
                    View Details <ArrowRight size={14} />
                  </Link>
                </motion.div>
              );
            })}
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="mt-12 text-center"
          >
            <Link
              to="/book-free-demo-class"
              className="inline-flex items-center gap-2 bg-red-600 text-white px-8 py-4 rounded-lg font-bold hover:bg-red-700 transition-colors shadow-lg shadow-red-600/20"
            >
              Book Free Demo Class <ArrowRight size={20} />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-slate-900 mb-4">
              Why Choose 6th Sense?
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              We don't just fill forms. We build careers.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                title: '500+ University Partnerships',
                description: 'Direct tie-ups with top universities worldwide for priority admissions',
                icon: GraduationCap
              },
              {
                title: 'Expert Counselors',
                description: 'Certified professionals with years of experience in study abroad guidance',
                icon: Users
              },
              {
                title: 'High Success Rate',
                description: 'Proven track record of visa approvals and successful student placements',
                icon: Award
              }
            ].map((item, index) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="text-center p-6"
                >
                  <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center text-red-600 mx-auto mb-4">
                    <Icon size={32} />
                  </div>
                  <h3 className="text-xl font-serif font-bold text-slate-900 mb-2">
                    {item.title}
                  </h3>
                  <p className="text-slate-600">
                    {item.description}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-red-600 to-red-700 text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-serif font-bold mb-4">
              Ready to Start Your Journey?
            </h2>
            <p className="text-xl text-red-100 mb-8">
              Book a free consultation and take the first step towards your dream education
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/book-free-counselling"
                className="inline-flex items-center justify-center gap-2 bg-white text-red-600 px-8 py-4 rounded-lg font-bold hover:bg-slate-50 transition-colors shadow-lg"
              >
                Free Counselling <ArrowRight size={20} />
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 bg-transparent border-2 border-white text-white px-8 py-4 rounded-lg font-bold hover:bg-white/10 transition-colors"
              >
                Contact Us
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Services;

